import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { fetcher } from '@weezevent/weezjs-core';

import { Role, UserV1 } from 'models';
import { usePermissions } from 'models/user/permissions';

import { useFilters, usePageSize, useCurrentPage, useSearch, handleSearchQuery } from 'hooks/useFilters';

import { WeezHeader, Icon, Card, Button, ContentCard, Filter, Table, TableCellDropdown, Dropdown, ToastContext, FilterSetController, Tooltip } from '@weezevent/nacre';

import { LoadingTable, Paginator, SauronEmptyCard, PaginationDropDown, QuickFilters } from 'components';
import NewUser from 'pages/usersV1/user/NewUser';
import RoleCreationModal from 'pages/usersV1/user/roles/RoleCreationModal';

import { listSelection, requestCount } from 'utils/utils';

import css from 'pages/usersV1/list.module.css';

const POSSIBLE_PER_PAGE_COUNT = [10, 50, 100];

const spec = {
    multiple: ['roles__slug__in'],
    fields: ['roles__slug__in', 'same_level_users', 'search'],
    date: [],
    form: [],
    aliases: {}
};

export const UsersList = ({ organizationId = null, useCase }) => {
    const [resourceKey, resourceKey_singular] = React.useMemo(() => [useCase, useCase === 'applications' ? 'application' : 'user'], [useCase]);
    const history = useHistory();
    const { t } = useTranslation();
    const user = usePermissions();
    const toast = React.useContext(ToastContext);
    const [userRoleModal, setUserRoleModal] = React.useState({ user: null, open: false, action: 'add' });
    const [usersIds, setUsersIds] = React.useState([]);
    const [selectedUsers, setSelectedUsers] = React.useState([]);
    const [newUserModal, setNewUserModal] = React.useState(false);

    const [filters, setFilters] = useFilters(spec, {});
    const [perPageCount, setPerPageCount] = usePageSize(10);
    const [currentPage, setCurrentPage] = useCurrentPage(0);
    const [search, setSearch] = useSearch();

    const [users, loadingUsers, syncUsers] = UserV1.useApiModel(
        {
            cache: false,
            strict: true,
            allow_cache: false,
            organizationId,
            useCase: useCase,
            query: {
                limit: perPageCount,
                offset: currentPage * perPageCount,
                search,
                ...handleSearchQuery(spec, filters, true)
            },
            onSuccess: response => {
                setUsersIds(response.map(user => user.username));
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [filters, perPageCount, currentPage, search, organizationId, useCase]
    );

    const usersCount = React.useMemo(() => {
        return requestCount(loadingUsers, users);
    }, [users, loadingUsers]);

    const handleQuickFilter = React.useCallback(
        checked => {
            if (checked) {
                setFilters({ ...filters, same_level_users: checked });
            } else {
                const { same_level_users, ...rest } = filters;
                setFilters(rest);
            }
        },
        [filters, setFilters]
    );

    const handleSelect = React.useCallback(
        user => {
            setSelectedUsers(listSelection(selectedUsers, usersIds, user));
        },
        [selectedUsers, usersIds, setSelectedUsers]
    );

    const [roles] = Role.useApiModel({ cache: false, allow_cache: false, organizationId }, [organizationId]);
    const selectedRoles = filters['roles__slug__in'];
    const roleOptions = React.useMemo(() => {
        const options = roles?.map(r => r.toOption()) ?? [];
        if (!selectedRoles?.size) return options;
        return [...options.filter(o => selectedRoles.has(o.name)), ...options.filter(o => !selectedRoles.has(o.name))];
    }, [roles, selectedRoles]);

    // To be able to remove a role on multiple users, the users must have at least one role in common
    // We can only perform batch remove on common shared roles
    const batchRemoveRoleStatus = React.useMemo(() => {
        if (!selectedUsers.length || !users) return { disabled: true, reason: 'no-selection' };

        const selectedUserObjects = users.filter(u => selectedUsers.includes(u.username));
        const allUserRoles = selectedUserObjects.map(u => u.user_roles || []);

        // If only one selected user has no role then we cannot do a batch remove
        if (allUserRoles.some(roles => roles.length === 0)) return { disabled: true, reason: 'no-roles' };

        const firstUserSlugs = allUserRoles[0].map(ur => ur.role.slug);
        // We take the first user's roles and make sure that we have at least one role in common among all the selected users
        const hasCommonRole = firstUserSlugs.some(slug => allUserRoles.every(roles => roles.some(ur => ur.role.slug === slug)));

        if (!hasCommonRole) return { disabled: true, reason: 'no-common-roles' };
        // if we are here then we can perform a batch remove and the button should not be disabled
        return { disabled: false, reason: null };
    }, [selectedUsers, users]);

    const getBatchRemoveTooltipMessage = () => {
        switch (batchRemoveRoleStatus.reason) {
            case 'no-roles':
                return t('sauron.user.remove-role-batch-disabled-no-roles');
            case 'no-common-roles':
                return t('sauron.user.remove-role-batch-disabled-no-common');
            default:
                return t('sauron.user.remove-role-batch-disabled-unknown');
        }
    };

    const handleBulkAction = React.useCallback(
        ({ action, config }) => {
            UserV1.executeBulkAction({
                type: action,
                organizationId,
                useCase,
                filters: { username__in: selectedUsers },
                config
            }).then(response => {
                toast.success(t(`sauron.user.bulk_actions.${action}.progress`, { count: selectedUsers.length }));

                UserV1.pollBulkAction(response)
                    .then(rsp => {
                        if (rsp.status === 'success') {
                            toast.success(t(`sauron.user.bulk_actions.${action}.success`, { count: rsp.summary.success }));
                        }
                    })
                    .catch(() => {
                        toast.error(t(`sauron.user.bulk_actions.${action}.error`));
                    })
                    .finally(() => {
                        syncUsers();
                    });
            });
        },
        [organizationId, useCase, selectedUsers, syncUsers]
    );

    return (
        <>
            <WeezHeader
                rightComponent={[<Button key={0} primary icon={<Icon name={'plus'} />} label={t(`sauron.${resourceKey}.add`)} onClick={() => setNewUserModal(true)} />]}
                title={t(`common.${resourceKey}`)}
                backLink={
                    organizationId && {
                        onClick: () => {
                            history.push(`/organizations`);
                        }
                    }
                }
            >
                {t(`sauron.${resourceKey}.list`)}
            </WeezHeader>

            <Card>
                <ContentCard>
                    <FilterSetController
                        total={usersCount}
                        labelFilters={t('sauron.users.filters.label-filter')}
                        labelTotal={t(`sauron.${resourceKey}.${resourceKey_singular}`, { count: usersCount })}
                        initialValues={filters}
                        onChange={setFilters}
                        onReset={() => {
                            setFilters({});
                            setCurrentPage(0);
                            setPerPageCount(10);
                        }}
                        search={{
                            onChange: (_, value) => setSearch(value),
                            handleReset: () => setSearch(''),
                            placeholder: t('common.search'),
                            value: search
                        }}
                    >
                        <Filter
                            index={0}
                            name={'roles__slug__in'}
                            isSearchable
                            multiple
                            labelName={t('sauron.users.filters.roles.label')}
                            labelCTA={t('sauron.users.filters.label-cta')}
                            options={roleOptions || []}
                        />
                    </FilterSetController>
                    <PaginationDropDown
                        title={t(`sauron.${resourceKey}.${resourceKey_singular}_action`, { count: selectedUsers.length })}
                        tooltip={t(`sauron.${resourceKey}.no_action_tooltip`)}
                        label={`sauron.${resourceKey}.page_size`}
                        selected={selectedUsers}
                        count={usersCount}
                        disabled={!selectedUsers.length}
                        limit={perPageCount}
                        pageSizes={POSSIBLE_PER_PAGE_COUNT}
                        onChange={setPerPageCount}
                        items={
                            <>
                                <Dropdown.Item item={t('sauron.user.add-role-batch')} onClick={() => setUserRoleModal({ user: null, open: true, action: 'add', batch: true })} />
                                <Tooltip
                                    visible={batchRemoveRoleStatus.disabled}
                                    triggerOnHover
                                    position={'top-start'}
                                    title={getBatchRemoveTooltipMessage()}
                                    margin={false}
                                    className={css['tooltip-full-width']}
                                >
                                    <Dropdown.Item
                                        item={t('sauron.user.remove-role-batch')}
                                        onClick={() => setUserRoleModal({ user: null, open: true, action: 'remove', batch: true })}
                                        disabled={batchRemoveRoleStatus.disabled}
                                    />
                                </Tooltip>
                            </>
                        }
                    />
                    {!organizationId && user.isAdmin() && useCase === 'users' && (
                        <QuickFilters>
                            <QuickFilters.Item label={t(`sauron.${resourceKey}.quick-filter`)} active={!!filters.same_level_users} onToggle={handleQuickFilter} />
                        </QuickFilters>
                    )}
                </ContentCard>
                {newUserModal && (
                    <NewUser organizationId={organizationId} useCaseItems={[resourceKey, resourceKey_singular]} open={newUserModal} onClose={() => setNewUserModal(false)} />
                )}
                <Table useNew selectable onSelectAll={() => handleSelect()} className={css['users-table']}>
                    <Table.THead>
                        <Table.Tr isHeader active={usersIds.length > 0 && usersIds.every(id => selectedUsers.includes(id))}>
                            <th className={css['users-table-medium']}>{t(`common.${resourceKey_singular}`)}</th>
                            <th className={css['users-table-medium']}>{t(`common.email`)}</th>
                            <th className={css['users-table-medium']}>{t(`common.roles`)}</th>
                            <th className={css['users-table-small']}>{t(`common.actions`)}</th>
                        </Table.Tr>
                    </Table.THead>
                    <Table.TBody>
                        {!loadingUsers &&
                            users?.length > 0 &&
                            users?.map(_user => (
                                <UserItem
                                    key={_user.username}
                                    authenticatedUser={user}
                                    user={_user}
                                    sync={syncUsers}
                                    selected={selectedUsers}
                                    onSelect={handleSelect}
                                    onOpen={setUserRoleModal}
                                    segment={[resourceKey, resourceKey_singular]}
                                />
                            ))}
                    </Table.TBody>
                </Table>
                <RoleCreationModal
                    open={userRoleModal.open}
                    user={userRoleModal.user}
                    users={userRoleModal.batch ? users.filter(u => selectedUsers.includes(u.username)) : null}
                    roles={roles}
                    removeMode={userRoleModal.action === 'remove'}
                    onClose={() => setUserRoleModal({ ...userRoleModal, open: false })}
                    sync={syncUsers}
                    onBulkAction={userRoleModal.batch ? handleBulkAction : null}
                />
                <LoadingTable loading={loadingUsers} height="30vh" />
                {!loadingUsers &&
                    (users?.length > 0 ? (
                        <Paginator currentPage={currentPage} onPageChange={setCurrentPage} pageSize={perPageCount} totalCount={usersCount} />
                    ) : (
                        <SauronEmptyCard title={t(`sauron.${resourceKey}.empty-card-title`)} subTitle={t(`sauron.${resourceKey}.empty-card-subtitle`)} />
                    ))}
            </Card>
        </>
    );
};

const UserItem = ({ user, sync, authenticatedUser, selected, onSelect, onOpen, segment }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const location = useLocation();
    const [resourceKey, resourceKey_singular] = segment;
    const resolvedUser = React.useMemo(() => {
        return {
            name: user.resolvedName,
            email: user.email || '-',
            roles: user.user_roles?.filter(role => role.context.level <= 3 && !role.context.parents?.some(p => p.value !== '*')).length
        };
    }, [user]);

    const handleRedirect = React.useCallback(() => {
        history.push(`${resourceKey}/${user.id || user.username}${location.search}`);
    }, [user, location.search]);

    const handleDeleteUser = React.useCallback(() => {
        const url = resourceKey === 'applications' ? `/ticket/v1/applications/${user.id}` : `/ticket/users/${user.username}`;

        fetcher
            .delete(url)
            .then(response => {
                if (response.status === 204) {
                    toast.success(t(`sauron.toasts.${resourceKey}.delete-${resourceKey_singular}-success`));
                }
            })
            .catch(() => {
                toast.error(t('sauron.toasts.error'));
            })
            .finally(() => {
                sync();
            });
    }, [user.id, user.username, resourceKey, resourceKey_singular]);

    return (
        <Table.Tr onSelected={() => onSelect(user.username)} active={selected.includes(user.username)} onClick={handleRedirect}>
            <Table.Td>{resolvedUser.name}</Table.Td>
            <Table.Td>{resolvedUser.email}</Table.Td>
            <Table.Td>{`${resolvedUser.roles} ${t(`sauron.${resourceKey}.role`, { count: resolvedUser.roles })}`}</Table.Td>

            <TableCellDropdown>
                <Dropdown.Item item={t(`sauron.${resourceKey_singular}.see-${resourceKey_singular}`)} onClick={handleRedirect} />
                <Dropdown.Item
                    item={t(`sauron.${resourceKey_singular}.add-role`)}
                    onClick={() => onOpen({ user, open: true, action: 'add' })}
                    disabled={!authenticatedUser.canManageRoles()}
                />
                <Tooltip
                    visible={!user.user_roles || user.user_roles.length === 0}
                    triggerOnHover
                    position={'top-start'}
                    title={t('sauron.user.remove-role-disabled-no-roles')}
                    margin={false}
                    className={css['tooltip-full-width']}
                >
                    <Dropdown.Item
                        item={t(`sauron.${resourceKey_singular}.remove-role`)}
                        onClick={() => onOpen({ user, open: true, action: 'remove' })}
                        disabled={!authenticatedUser.canManageRoles() || !user.user_roles || user.user_roles.length === 0}
                    />
                </Tooltip>
                {authenticatedUser.isAdmin() && resourceKey_singular === 'application' && (
                    <Dropdown.Item deleteItem={true} item={t(`sauron.${resourceKey_singular}.delete`)} onClick={() => handleDeleteUser()} />
                )}
            </TableCellDropdown>
        </Table.Tr>
    );
};
